import {
  Banner,
  Box,
  Breadcrumb,
  Flex,
  Header,
  Icon,
  Link,
  Text,
} from '@driftt/dds'
import { faArrowUpRightFromSquare } from '@driftt/dds.icons/light/faArrowUpRightFromSquare'
import { faSparkles } from '@driftt/dds.icons/solid/faSparkles'
import { DroidProbe } from '@driftt/droid-probe'
import { Subnav as SideNav } from '@driftt/tide-subnav'
import React, { useEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import { Link as ReactRouterLink, NavLink } from 'react-router-dom'

import usePageTitle from 'hooks/usePageTitle'
import driftRectangle from 'images/drift-rectangle.svg'
import droidView from 'images/droid-view.png'
import { getSubNavNodes } from 'utils/getSubNavNodes'
import { isEmptyOrNil } from 'utils/index'
import { trackEvent } from 'utils/tracking'

const GptNodeTestingLayout = () => {
  const queryClient = useQueryClient()
  const orgId = useSelector((state) => state.user.orgId)
  const user = useSelector((state) => state.user)
  const [loadReady, setLoadReady] = useState(false)
  const botUser = useSelector(
    (state) =>
      state.org.users.filter((orgUser) => {
        return orgUser.bot
      })[0]
  )
  const pageVisitTracked = useRef(false)

  const [sidebarIsOpen, setSidebarIsOpen] = useState(false)
  const [gptServiceError, setGptServiceError] = useState(false)
  const SUB_NAV_NODES = getSubNavNodes()

  usePageTitle('Drift | AI responses testing')

  if (!pageVisitTracked.current) {
    pageVisitTracked.current = true
    trackEvent('Ai Testing Widget Visited', {
      orgId,
      user,
    })
  }
  const handleGptServiceError = () => setGptServiceError(true)

  useEffect(() => {
    if (!isEmptyOrNil(user) && !isEmptyOrNil(botUser) && !loadReady) {
      setTimeout(() => setLoadReady(true), 100)
    }
  }, [botUser, loadReady, user])

  return (
    <Box css={{ width: '100%', height: '100%' }}>
      <Flex direction="row" fullWidth fullHeight gap={0}>
        <SideNav
          open={sidebarIsOpen}
          nodes={SUB_NAV_NODES}
          title="Chatbot"
          NavLink={NavLink}
          style={{ height: '100%' }}
          onToggle={() => setSidebarIsOpen(!sidebarIsOpen)}
        />
        <Flex direction="column" gap={0} fullWidth fullHeight>
          <Flex direction="column" gap={0} css={{ padding: '$2 $6' }} fullWidth>
            <Flex
              direction="row"
              fullWidth
              gap={0}
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Breadcrumb.Root>
                  <Breadcrumb.Link as={ReactRouterLink} to="/playbooks">
                    Playbooks
                  </Breadcrumb.Link>
                  <Breadcrumb.Link
                    as={ReactRouterLink}
                    to="/playbooks/llm-testing"
                  >
                    AI responses testing
                  </Breadcrumb.Link>
                </Breadcrumb.Root>
                <Flex alignItems="center">
                  <Icon icon={faSparkles} color="purple8" />
                  <Header> AI responses testing</Header>
                </Flex>
              </Box>
              <Link href="/playbooks" target="_blank" type="subtle">
                <Icon
                  icon={faArrowUpRightFromSquare}
                  css={{ marginRight: '$1' }}
                />
                Manage playbooks
              </Link>
            </Flex>
          </Flex>
          {gptServiceError && (
            <Banner.Root variant="warning">
              <Banner.Content>
                <Text>
                  Oh no! Something appears to have gone wrong. Please refresh
                  the page and try again
                </Text>
              </Banner.Content>
            </Banner.Root>
          )}
          <Box
            css={{
              width: '100%',
              height: '94%',
              overflow: 'hidden',
              overflowY: 'hidden',
              background: '$gray2',
            }}
          >
            {loadReady && (
              <DroidProbe
                user={user}
                driftRectangle={driftRectangle}
                droidView={droidView}
                botUser={botUser}
                queryClient={queryClient}
                onGenAiServiceError={handleGptServiceError}
              />
            )}
          </Box>
        </Flex>
      </Flex>
    </Box>
  )
}

export default GptNodeTestingLayout
